// Backend response interface (matches what the API actually returns)
interface BackendUserProfile {
	id: string;
	username: string;
	email: string;
	first_name?: string;
	last_name?: string;
	location?: string;
	company?: string;
	bio?: string;
	avatar?: string;
	role: number;
	// Token system information
	tokens_available: number;
	tokens_used: number;
	tokens_purchased: number;
	last_token_purchase?: string;
	created_at: string;
	updated_at: string;
}

export interface UserProfile {
	id: string;
	username: string;
	email: string;
	firstName?: string;
	lastName?: string;
	location?: string;
	company?: string;
	bio?: string;
	avatar?: string;
	role: number;
	// Token system information
	tokens: {
		available: number;
		used: number;
		purchased: number;
		lastPurchase?: string;
	};
	created_at: string;
	updated_at: string;
	preferences: UserPreferences;
}

export interface UserPreferences {
	theme: "light" | "dark" | "system";
	language: string;
	notifications: NotificationPreferences;
	privacy: PrivacyPreferences;
}

export interface NotificationPreferences {
	email: boolean;
	push: boolean;
	sms: boolean;
}

export interface PrivacyPreferences {
	profileVisibility: "public" | "private" | "friends";
	showEmail: boolean;
}

export interface PasswordChangeRequest {
	currentPassword: string;
	newPassword: string;
}

export interface ProfileUpdateRequest {
	username?: string;
	firstName?: string;
	lastName?: string;
	location?: string;
	company?: string;
	bio?: string;
	avatar?: string;
}

export interface PreferencesUpdateRequest {
	theme?: "light" | "dark" | "system";
	language?: string;
	notifications?: Partial<NotificationPreferences>;
	privacy?: Partial<PrivacyPreferences>;
}

class UserApi {
	private baseUrl: string;

	constructor() {
		this.baseUrl = process.env.REACT_APP_API_URL || "https://core.gamura.ai";
	}

	private async request<T>(
		endpoint: string,
		options: RequestInit = {}
	): Promise<T> {
		const url = `${this.baseUrl}${endpoint}`;
		const token = localStorage.getItem("gamora_token");

		const config: RequestInit = {
			...options,
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
				...options.headers,
			},
		};

		try {
			const response = await fetch(url, config);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.detail || `HTTP error! status: ${response.status}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error(`API request failed for ${endpoint}:`, error);
			throw error;
		}
	}

	// Real API endpoints
	async getProfile(): Promise<UserProfile> {
		const profile = await this.request<BackendUserProfile>("/api/v1/profile/");

		// Transform backend response to frontend format
		return {
			id: profile.id,
			username: profile.username,
			email: profile.email,
			firstName: profile.first_name,
			lastName: profile.last_name,
			location: profile.location,
			company: profile.company,
			bio: profile.bio,
			avatar: profile.avatar,
			role: profile.role,
			tokens: {
				available: profile.tokens_available,
				used: profile.tokens_used,
				purchased: profile.tokens_purchased,
				lastPurchase: profile.last_token_purchase,
			},
			created_at: profile.created_at,
			updated_at: profile.updated_at,
			preferences: {
				theme: "system",
				language: "en",
				notifications: {
					email: true,
					push: true,
					sms: false,
				},
				privacy: {
					profileVisibility: "public",
					showEmail: true,
				},
			},
		};
	}

	async updateProfile(updates: ProfileUpdateRequest): Promise<UserProfile> {
		// Transform frontend format to backend format
		const backendUpdates = {
			username: updates.username,
			first_name: updates.firstName,
			last_name: updates.lastName,
			location: updates.location,
			company: updates.company,
			bio: updates.bio,
			avatar: updates.avatar,
		};

		const profile = await this.request<BackendUserProfile>("/api/v1/profile/", {
			method: "PUT",
			body: JSON.stringify(backendUpdates),
		});

		// Transform backend response to frontend format
		return {
			id: profile.id,
			username: profile.username,
			email: profile.email,
			firstName: profile.first_name,
			lastName: profile.last_name,
			location: profile.location,
			company: profile.company,
			bio: profile.bio,
			avatar: profile.avatar,
			role: profile.role,
			tokens: {
				available: profile.tokens_available,
				used: profile.tokens_used,
				purchased: profile.tokens_purchased,
				lastPurchase: profile.last_token_purchase,
			},
			created_at: profile.created_at,
			updated_at: profile.updated_at,
			preferences: {
				theme: "system",
				language: "en",
				notifications: {
					email: true,
					push: true,
					sms: false,
				},
				privacy: {
					profileVisibility: "public",
					showEmail: true,
				},
			},
		};
	}

	async changePassword(request: PasswordChangeRequest): Promise<void> {
		await this.request("/api/v1/profile/change-password", {
			method: "POST",
			body: JSON.stringify({
				current_password: request.currentPassword,
				new_password: request.newPassword,
			}),
		});
	}

	async updatePreferences(
		updates: PreferencesUpdateRequest
	): Promise<UserPreferences> {
		// For now, we'll use mock data since preferences aren't implemented in backend yet
		// TODO: Implement preferences in backend
		return this.mockUpdatePreferences(updates);
	}

	// Mock API methods (fallback for development)
	getMockProfile(): UserProfile {
		return {
			id: "1",
			username: "user",
			email: "user@example.com",
			firstName: "User",
			lastName: "Account",
			location: "",
			company: "",
			bio: "Welcome to your profile! Add some information about yourself.",
			avatar: "",
			role: 20,
			tokens: {
				available: 0,
				used: 0,
				purchased: 0,
				lastPurchase: undefined,
			},
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
			preferences: {
				theme: "system",
				language: "en",
				notifications: {
					email: true,
					push: true,
					sms: false,
				},
				privacy: {
					profileVisibility: "public",
					showEmail: true,
				},
			},
		};
	}

	async mockGetProfile(): Promise<UserProfile> {
		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 500));
		return this.getMockProfile();
	}

	async mockUpdateProfile(updates: ProfileUpdateRequest): Promise<UserProfile> {
		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 500));

		const currentProfile = this.getMockProfile();
		const updatedProfile = { ...currentProfile, ...updates };

		// Update mock data
		Object.assign(currentProfile, updatedProfile);

		return updatedProfile;
	}

	async mockChangePassword(request: PasswordChangeRequest): Promise<void> {
		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 500));

		if (request.currentPassword !== "current") {
			throw new Error("Current password is incorrect");
		}

		if (request.newPassword.length < 8) {
			throw new Error("Password must be at least 8 characters long");
		}
	}

	async mockUpdatePreferences(
		updates: PreferencesUpdateRequest
	): Promise<UserPreferences> {
		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 500));

		const currentPreferences = this.getMockProfile().preferences;

		// Merge updates with current preferences
		const updatedPreferences = { ...currentPreferences };

		if (updates.theme !== undefined) {
			updatedPreferences.theme = updates.theme;
		}

		if (updates.language !== undefined) {
			updatedPreferences.language = updates.language;
		}

		if (updates.notifications) {
			updatedPreferences.notifications = {
				...updatedPreferences.notifications,
				...updates.notifications,
			};
		}

		if (updates.privacy) {
			updatedPreferences.privacy = {
				...updatedPreferences.privacy,
				...updates.privacy,
			};
		}

		return updatedPreferences;
	}
}

export const userApi = new UserApi();
